<!-- Global Team Dialog Manager -->
<!-- Renders team add/manage dialogs at app root level, independent of sidebar visibility -->
<!-- This ensures dialogs work on mobile even when sidebar is collapsed/hidden -->
<script setup lang="ts">
import { toast } from 'vue-sonner';

import { useTeamDialog } from '@/composables/useTeamDialog';

import NavTeamAddComponent from '../app-sidebar/nav-team-add.vue';
import NavTeamManageComponent from '../app-sidebar/nav-team-manage.vue';

const { isTeamDialogOpen, dialogMode, selectedOrganizationId, closeTeamDialog } = useTeamDialog();

// Dialog close control - prevent accidental closing during critical operations
const preventDialogClose = ref(false);

// Computed component based on dialog mode
const showComponent = computed(() => {
  if (dialogMode.value === 'add') {
    return NavTeamAddComponent;
  } else if (dialogMode.value === 'manage') {
    return NavTeamManageComponent;
  }
  return null;
});

// Handle dialog open/close state changes
function handleDialogOpenChange(open: boolean) {
  // If trying to close dialog but prevention is active, keep it open
  if (!open && preventDialogClose.value) {
    // Force the dialog to stay open by immediately setting it back
    nextTick(() => {
      isTeamDialogOpen.value = true;
    });
    toast.warning('Cannot close dialog', {
      position: 'top-center',
      description: 'Please complete or cancel the current operation first.',
    });
    return;
  }

  // Normal behavior
  isTeamDialogOpen.value = open;

  // Reset states when dialog closes
  if (!open) {
    preventDialogClose.value = false;
    closeTeamDialog();
  }
}

// Always prevent escape and backdrop closing for team management dialogs
// Users should only close via the buttons which have proper confirmation logic
function handleEscapeKeyDown(event: KeyboardEvent) {
  event.preventDefault();
  toast.info('Use the Close button to exit', {
    position: 'top-center',
    description: 'Please use the Close or Cancel button to safely exit this dialog.',
  });
}

// Always prevent backdrop closing for team management dialogs
function handlePointerDownOutside(event: Event) {
  event.preventDefault();
  toast.info('Use the Close button to exit', {
    position: 'top-center',
    description: 'Please use the Close or Cancel button to safely exit this dialog.',
  });
}
</script>

<template>
  <UiDialog :open="isTeamDialogOpen" @update:open="handleDialogOpenChange">
    <UiDialogScrollContent
      @escape-key-down="handleEscapeKeyDown"
      @pointer-down-outside="handlePointerDownOutside"
    >
      <component
        :is="showComponent"
        :organization-id="selectedOrganizationId"
        @close="closeTeamDialog"
        @prevent-close="preventDialogClose = $event"
      />
    </UiDialogScrollContent>
  </UiDialog>
</template>
